<script setup lang="ts">
const colorMode = useColorMode();

interface ProcessStep {
  // id is the display number (e.g. "1", "2"), not a UUID
  id: string;
  svgIcon?: string;
  title: string;
  description: string;
}

const props = withDefaults(defineProps<{
  steps: ProcessStep[];
  heading?: string;
  subcopy?: string;
  ctaText?: string;
  ctaLink?: string | null;
}>(), {
  heading: '',
  subcopy: '',
  ctaText: '',
  ctaLink: null,
});

const emit = defineEmits<{
  'cta-clicked': [];
}>();

const gridCols = computed(() => {
  const len = props.steps.length;
  if (len <= 2) return 'grid-cols-1 sm:grid-cols-2';
  if (len === 3) return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';
  return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4';
});

const isLastCard = (index: number) => index === props.steps.length - 1;
</script>

<template>
  <section
    class="mt-24 lg:mt-[50px] mb-16 lg:mb-[100px] flex flex-col items-center justify-center"
    aria-label="Our Process"
  >
    <div class="text-center mb-10">
      <h2
        v-if="heading"
        class="text-[28px] lg:text-[48px] font-semibold leading-tight text-center font-avant-demi text-transparent bg-[linear-gradient(92deg,#12DBFF_-3.57%,#BA55FE_80.72%)] bg-clip-text"
      >
        {{ heading }}
      </h2>
      <p
        v-if="subcopy"
        class="text-[16px] lg:text-[18px] font-normal tracking-[0.36px] mt-4 text-center px-4 font-avant"
        :class="colorMode.preference === 'dark' ? 'text-[#D9D9D9]' : 'text-[#000000]'"
      >
        {{ subcopy }}
      </p>
    </div>

    <!-- Steps grid with corner squares -->
    <div :class="['grid', 'gap-6', 'mt-10', 'px-4', 'max-w-[1100px]', 'mx-auto', gridCols]">
      <div
        v-for="(step, index) in steps"
        :key="step.id"
        class="relative"
      >
        <!-- Card -->
        <article
          class="p-6 flex flex-col border font-avant w-full lg:w-[242.447px] lg:h-[261.282px]"
          :class="colorMode.preference === 'dark' ? 'border-[#3A2A7B] bg-[#1B022B]' : 'border-[#E7CFFF] bg-[linear-gradient(32deg,#FFF_-74.45%,#FFF_100.91%)]'"
        >
          <!-- Step number (only when no icon) -->
          <span
            v-if="!step.svgIcon"
            class="mb-4 block font-avant text-[20px] font-normal"
            :class="colorMode.preference === 'dark' ? 'text-[#AF66E4]' : 'text-[#613FAA]'"
          >
            {{ step.id }}
          </span>

          <!-- Icon -->
          <WrIcon
            v-if="step.svgIcon"
            :name="step.svgIcon"
            class="mb-3 size-8 max-sm:size-6"
          />

          <h3
            class="font-semibold font-avant-demi text-[18px] leading-normal"
            :class="colorMode.preference === 'dark' ? 'text-white' : 'text-[#613FAA]'"
          >
            <span class="whitespace-pre-line">{{ step.title }}</span>
          </h3>
          <p
            class="mt-auto font-avant text-[15px] leading-5"
            :class="colorMode.preference === 'dark' ? 'text-[#AE5DE8]' : 'text-black'"
          >
            {{ step.description }}
          </p>
        </article>

        <!-- Top-left square (first card only) -->
        <span v-if="index === 0" class="hidden lg:block absolute top-0 left-0 w-[5.62px] h-[5.577px] bg-[#4902A6] -translate-x-1/2 -translate-y-1/2 z-10"></span>
        <!-- Top-right square (card 1) — shifted right to bridge to next card -->
        <span v-if="index === 0 && !isLastCard(0)" class="hidden lg:block absolute top-0 right-0 w-[5.62px] h-[5.577px] bg-[#4902A6] translate-x-[480%] -translate-y-1/2 z-10"></span>
        <!-- Top-right square (card 2) -->
        <span v-if="index === 1 && !isLastCard(1)" class="hidden lg:block absolute top-0 right-0 w-[5.62px] h-[5.577px] bg-[#4902A6] translate-x-[480%] -translate-y-1/2 z-10"></span>
        <!-- Top-right square (card 3) -->
        <span v-if="index === 2 && !isLastCard(2)" class="hidden lg:block absolute top-0 right-0 w-[5.62px] h-[5.577px] bg-[#4902A6] translate-x-[480%] -translate-y-1/2 z-10"></span>
        <!-- Top-right square (card 4) — only if not last -->
        <span v-if="index === 3 && !isLastCard(3)" class="hidden lg:block absolute top-0 right-0 w-[5.62px] h-[5.577px] bg-[#4902A6] translate-x-[630%] -translate-y-1/2 z-10"></span>
        <!-- Bottom-left square (first card only) -->
        <span v-if="index === 0" class="hidden lg:block absolute bottom-0 left-0 w-[5.62px] h-[5.577px] bg-[#4902A6] -translate-x-1/2 translate-y-1/2 z-10"></span>
        <!-- Bottom-right square (card 1) — shifted right to bridge to next card -->
        <span v-if="index === 0 && !isLastCard(0)" class="hidden lg:block absolute bottom-0 right-0 w-[5.62px] h-[5.577px] bg-[#4902A6] translate-x-[480%] translate-y-1/2 z-10"></span>
        <!-- Bottom-right square (card 2) -->
        <span v-if="index === 1 && !isLastCard(1)" class="hidden lg:block absolute bottom-0 right-0 w-[5.62px] h-[5.577px] bg-[#4902A6] translate-x-[480%] translate-y-1/2 z-10"></span>
        <!-- Bottom-right square (card 3) -->
        <span v-if="index === 2 && !isLastCard(2)" class="hidden lg:block absolute bottom-0 right-0 w-[5.62px] h-[5.577px] bg-[#4902A6] translate-x-[480%] translate-y-1/2 z-10"></span>
        <!-- Bottom-right square (card 4) — only if not last -->
        <span v-if="index === 3 && !isLastCard(3)" class="hidden lg:block absolute bottom-0 right-0 w-[5.62px] h-[5.577px] bg-[#4902A6] translate-x-[630%] translate-y-1/2 z-10"></span>
      </div>
    </div>

    <!-- CTA Button -->
    <div v-if="ctaText" class="flex justify-center mt-10">
      <WrButton
        :link="ctaLink || undefined"
        @click="!ctaLink ? emit('cta-clicked') : undefined"
        container-class="h-[40px] lg:h-[48px]"
        append-class="text-md lg:font-normal px-3 lg:px-5"
      >
        {{ ctaText }}
      </WrButton>
    </div>
  </section>
</template>
